/**
 * The ★ map rules overlay's title, over the top of the map, and its "tap
 * the map to return" hint (design v3, Variant 1).
 * The scrim itself is drawn inside the map's SVG (RulesOverlayBoardLayer).
 */
import React from 'react'
import { Text, View } from 'react-native'
import { MaterialCommunityIcons } from '@expo/vector-icons'
import type { OverlayModel } from '../../../lib/ruleOverlayModel'
import type { OverlayPalette } from '../../../lib/rulesOverlayTheme'

const TITLE_INK = '#f4ecd3'

export function RulesOverlayTitle({ model, palette, compact, top, onClose }: {
  model: OverlayModel
  palette: OverlayPalette
  compact: boolean
  top: number
  /** No visible close button: a tap on the map, Esc or the segment closes the
   *  overlay. Screen readers get a Close action and the escape gesture here. */
  onClose: () => void
}) {
  const closeLabel = 'Close special rules'
  return (
    <View pointerEvents="box-none" style={{ position: 'absolute', left: 0, right: 0, top }}>
      {/* Screen readers hear the whole rule list (the map's specialRules text) here. */}
      <View
        pointerEvents="none"
        accessible
        accessibilityRole="header"
        accessibilityLabel={model.accessibilitySummary}
        accessibilityActions={[{ name: 'escape', label: closeLabel }, { name: 'dismiss', label: closeLabel }]}
        onAccessibilityAction={(event) => { if (event.nativeEvent.actionName === 'escape' || event.nativeEvent.actionName === 'dismiss') onClose() }}
        onAccessibilityEscape={onClose}
        testID="rules-overlay-title"
        style={{ position: 'absolute', left: compact ? 16 : 22, top: 0, right: compact ? 16 : 22 }}
      >
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
          <MaterialCommunityIcons name="star" size={compact ? 17 : 20} color={palette.amber} />
          <Text
            className="font-stencil"
            style={{ fontSize: compact ? 17 : 20, lineHeight: compact ? 20 : 24, color: TITLE_INK, textShadowColor: 'rgba(0,0,0,0.6)', textShadowRadius: 6 }}
          >
            {model.title}
          </Text>
        </View>
        {model.subtitle ? (
          <Text testID="rules-overlay-subtitle" className="font-typed" numberOfLines={2} style={{ fontSize: 13, color: TITLE_INK, opacity: 0.85, textShadowColor: 'rgba(0,0,0,0.6)', textShadowRadius: 6 }}>
            {model.subtitle}
          </Text>
        ) : null}
      </View>
    </View>
  )
}

export function RulesOverlayHint({ desktop }: { desktop: boolean }) {
  return (
    <View pointerEvents="none" style={{ alignSelf: 'center', backgroundColor: 'rgba(0,0,0,0.45)', borderRadius: 999, paddingHorizontal: 12, paddingVertical: 5 }}>
      <Text className="font-stencil" style={{ fontSize: 10, letterSpacing: 0.4, color: TITLE_INK }}>
        {desktop ? 'Click the map or press Esc to close' : 'Tap the map to return'}
      </Text>
    </View>
  )
}
